import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import { apiUrl, API_ENDPOINTS, buildQueryParams } from '../Services/api'
import { useSnackbar } from 'notistack'
import {
  DEBOUNCE_DELAY,
  DEFAULT_PAGE,
  SORT_OPTIONS,
  ORDER_OPTIONS,
} from '../constants'

/**
 * Hook personnalisé pour gérer la pagination et le chargement des livres
 */
export const useBooks = (options = {}) => {
  const { viewMode = 'default' } = options
  const { token, isAuthenticated, deleteTokenCookie } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(false)
  const [refresh, setRefresh] = useState(false)

  // Filtres et pagination
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.TITLE)
  const [order, setOrder] = useState(ORDER_OPTIONS.ASC)
  const [page, setPage] = useState(DEFAULT_PAGE)
  const [totalPages, setTotalPages] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [ratings, setRatings] = useState([])

  // Debounce de la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, DEBOUNCE_DELAY)

    return () => clearTimeout(timer)
  }, [search])

  // Réinitialiser la page lors d'une nouvelle recherche ou filtre
  useEffect(() => {
    if (page !== DEFAULT_PAGE) {
      setPage(DEFAULT_PAGE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, ratings])

  // Scroll vers le haut lors du changement de page
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      // Ignorer les erreurs dans les environnements sans window
    }
  }, [page])

  // Charger le nombre total de pages
  const fetchCount = useCallback(async () => {
    if (!isAuthenticated || !token) return

    try {
      const endpoint = debouncedSearch
        ? API_ENDPOINTS.BOOKS.SEARCH_COUNT
        : API_ENDPOINTS.BOOKS.MY_BOOKS_COUNT

      const url = new URL(apiUrl(endpoint))
      if (debouncedSearch) {
        url.searchParams.set('q', debouncedSearch)
      }
      if (ratings && ratings.length > 0) {
        ratings.forEach(r => url.searchParams.append('rating', String(r)))
      }

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.status === 401) {
        deleteTokenCookie()
        return
      }

      if (!response.ok) {
        throw new Error(await response.text())
      }

      const data = await response.json()
      const idx =
        data && typeof data.index !== 'undefined' ? Number(data.index) : NaN
      const pages = !isNaN(idx) && idx >= 0 ? Math.max(0, idx + 1) : 0

      setTotalPages(pages)

      // Ajuster la page courante si hors limites
      if (pages <= 0 && page !== DEFAULT_PAGE) {
        setPage(DEFAULT_PAGE)
      } else if (page > pages - 1) {
        setPage(pages - 1)
      }
    } catch (err) {
      enqueueSnackbar(err.message || 'Erreur lors du chargement du nombre de livres.', { variant: 'error' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated, debouncedSearch, page, ratings, enqueueSnackbar])

  // Charger les livres
  const fetchBooks = useCallback(async () => {
    if (!isAuthenticated || !token) return

    setLoading(true)

    try {
      const endpoint = debouncedSearch
        ? API_ENDPOINTS.BOOKS.SEARCH
        : API_ENDPOINTS.BOOKS.MY_BOOKS

      const url = new URL(apiUrl(endpoint))
      const params = buildQueryParams({
        sortBy,
        order,
        page: viewMode === 'series' ? -1 : page,
        search: debouncedSearch,
        ratings,
      })
      url.search = params

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.status === 401) {
        deleteTokenCookie()
        return
      }

      if (!response.ok) {
        throw new Error(await response.text())
      }

      const data = await response.json()
      setBooks(data)
    } catch (err) {
      enqueueSnackbar(err.message || 'Erreur lors du chargement des livres.', { variant: 'error' })
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated, sortBy, order, page, debouncedSearch, ratings, enqueueSnackbar, viewMode])

  // Charger le nombre de pages
  useEffect(() => {
    fetchCount()
  }, [fetchCount, refresh])

  // Charger les livres
  useEffect(() => {
    fetchBooks()
  }, [fetchBooks, refresh])

  // Réinitialiser le refresh
  useEffect(() => {
    if (refresh) {
      setRefresh(false)
    }
  }, [refresh])

  const clearSearch = useCallback(() => {
    setSearch('')
    setDebouncedSearch('')
    setPage(DEFAULT_PAGE)
  }, [])

  return {
    books,
    loading,
    sortBy,
    setSortBy,
    order,
    setOrder,
    page,
    setPage,
    totalPages,
    search,
    setSearch,
    clearSearch,
    refresh,
    setRefresh,
    ratings,
    setRatings,
  }
}
